/**
 * Saved vehicle store (RAZ-327), shared by the finder block, the header chip and
 * panel (RAZ-337) and the PDP fitment card (RAZ-338).
 *
 * The vehicle is persisted client side only: Liquid cannot read it, so every
 * vehicle-aware surface renders on the client and the pages stay CDN-cacheable.
 *
 * Alpine is imported rather than read off `window`, because the store glob in
 * src/entrypoints/theme.js builds every store before `window.Alpine` is assigned.
 * The import resolves to the same Alpine instance the entry point uses.
 */

import Alpine from 'alpinejs';

import { labels as vehicleLabels, refreshVehicle } from '../utils/vehicleResolver.js';

/** localStorage key @alpinejs/persist writes the saved vehicle to. */
export const PERSIST_KEY = 'rb_vehicle';

/** Sliding time-to-live used when the theme setting is missing or unusable. */
const DEFAULT_SAVED_DAYS = 180;

/**
 * `clear()` reason for a vehicle the catalogue no longer holds, as opposed to one the
 * shopper removed or one that timed out. Exported because the surfaces that listen for
 * the clear have to tell those apart before they speak.
 */
export const CLEARED_BY_CATALOGUE = 'catalogue';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

/** Epoch seconds, the unit buildVehicle() writes into `savedAt`. */
function nowInSeconds() {
  return Math.floor(Date.now() / 1000);
}

/**
 * How long a saved vehicle survives without a visit, in milliseconds.
 * @returns {number}
 */
function savedTtlMs() {
  let days = DEFAULT_SAVED_DAYS;
  try {
    const configured = Number(window.themeSettings?.vehicleSavedDays);
    if (Number.isFinite(configured) && configured > 0) days = configured;
  } catch {
    // No themeSettings on this page; the default time-to-live is correct.
  }
  return days * DAY_IN_MS;
}

/**
 * Fire a window event, never letting a listener's error break the store.
 * @param {string} name
 * @param {Object} detail
 */
function announce(name, detail) {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch (error) {
    if (window.enableDebugMode) { console.warn(`stores/vehicle.js: ${name} listener failed`, error); }
  }
}

/**
 * Remove a saved vehicle that is not valid JSON, before @alpinejs/persist reads it.
 *
 * Persist JSON.parses the key with no guard, inside Alpine.store(), and the store glob in
 * src/entrypoints/theme.js runs before Alpine.start() is wired up. One truncated value
 * therefore stopped every Alpine component on the site until the shopper cleared their
 * storage. Losing a vehicle nobody can read is the lesser harm.
 *
 * @param {Storage|null} storage usually window.localStorage
 * @returns {boolean} true when something was removed
 */
export function discardUnreadableVehicle(storage) {
  let raw = null;
  try {
    raw = storage?.getItem(PERSIST_KEY) ?? null;
  } catch {
    // Storage that cannot be read cannot be repaired either.
    return false;
  }
  if (raw === null) return false;

  try {
    JSON.parse(raw);
    return false;
  } catch {
    try {
      storage.removeItem(PERSIST_KEY);
      return true;
    } catch {
      return false;
    }
  }
}

/** window.localStorage, or null where reading it throws (blocked site data). */
function localStorageOrNull() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export default {
  name: 'vehicle',
  store() {
    discardUnreadableVehicle(localStorageOrNull());

    return {
      // The saved vehicle, or null. Shape: see buildVehicle() in vehicleResolver.js.
      vehicle: Alpine.$persist(null).as(PERSIST_KEY),

      // True when the catalogue no longer holds every fitment this vehicle named, so
      // the surfaces downgrade a "fits" claim to "please confirm". Never persisted:
      // it is re-derived on any page that loads the catalogue.
      isStale: false,

      init() {
        this.expireOrSlide();
        this.watchOtherTabs();
      },

      /* ---------------------------------------------------------------- */
      /* Reading                                                          */
      /* ---------------------------------------------------------------- */

      get hasVehicle() {
        return this.vehicle !== null && this.vehicle !== undefined;
      },

      get isComplete() {
        return this.vehicle?.complete === true;
      },

      /** Placeholder map for every vehicle-aware copy string. */
      get labels() {
        return vehicleLabels(this.vehicle ?? null);
      },

      /** "Toyota HiLux 8th Gen SR5", the sentence the PLP bar and PDP card show. */
      get sentence() {
        return this.labels.sentence;
      },

      /* ---------------------------------------------------------------- */
      /* Writing                                                          */
      /* ---------------------------------------------------------------- */

      /**
       * Save a vehicle built by buildVehicle().
       * @param {Object|null} vehicle
       * @returns {Object|null} the vehicle as stored
       */
      set(vehicle) {
        if (!vehicle) {
          this.clear();
          return null;
        }

        const saved = { ...vehicle, savedAt: vehicle.savedAt ?? nowInSeconds() };
        this.vehicle = saved;
        this.isStale = false;

        // `fitmentId` is the name the published analytics contract uses, and the one the
        // rest of the codebase uses; `id` is kept alongside it for any listener already
        // written against the earlier payload.
        announce('arc:action:vehicleSet:success', {
          vehicle: saved,
          fitmentId: saved.id ?? null,
          id: saved.id ?? null,
          year: saved.year ?? null,
          make: saved.make?.h ?? null,
          model: saved.model?.h ?? null,
          series: saved.series?.h ?? null,
          complete: saved.complete === true,
          destination: saved.dest ?? null,
          sentence: vehicleLabels(saved).sentence,
        });

        return saved;
      },

      /**
       * Forget the saved vehicle.
       * @param {string} [reason] 'shopper' for a deliberate removal, 'expired' for the
       *   time-to-live, 'catalogue' for a vehicle the catalogue no longer holds
       */
      clear(reason = 'shopper') {
        this.vehicle = null;
        this.isStale = false;
        announce('arc:action:vehicleCleared', { reason });
      },

      /* ---------------------------------------------------------------- */
      /* Catalogue reconciliation                                         */
      /* ---------------------------------------------------------------- */

      /**
       * Re-resolve the saved vehicle against the live catalogue, on the pages that
       * load it.
       *
       * refreshVehicle() answers in two different ways and they must not be conflated:
       *
       *  - a vehicle plus `isStale` means the vehicle still exists but some of the
       *    fitments it named have gone. It is kept, re-resolved and flagged, so the PDP
       *    card downgrades "Fits your HiLux" to "Please confirm" rather than either
       *    lying or forgetting what the shopper told us;
       *  - a null vehicle means nothing it named survived — the content team deleted or
       *    renamed the model, the series or every fitment. There is no honest reading of
       *    a vehicle the catalogue has lost, so it is dropped.
       *
       * Keeping the null case and only flagging it — which is what this did — left the
       * store holding a vehicle that exists nowhere but localStorage, and every surface
       * that reads the store rather than the catalogue went on naming it: the header chip
       * read "TOYOTA HILUX / 99TH GEN · GHOST", the collection bar offered covers "for
       * your 99th Gen GHOST" and the PDP card told the shopper a cover that genuinely
       * fits their truck was "not listed for your 99th Gen GHOST" — while the finder,
       * which reads the catalogue, had already emptied itself. Two surfaces disagreeing
       * about whether a shopper has a vehicle is worse than either answer alone.
       *
       * The clear is announced with its own reason, so a listener can tell a vehicle the
       * shopper removed from one the catalogue lost: the first is worth speaking aloud,
       * the second happened before they touched anything.
       *
       * @param {Object} data index from indexData()
       * @returns {Object|null} the saved vehicle after reconciliation, or null when the
       *   catalogue no longer holds it
       */
      refresh(data) {
        if (!data || !this.hasVehicle) return this.vehicle ?? null;

        let result = null;
        try {
          result = refreshVehicle(data, this.vehicle);
        } catch (error) {
          if (window.enableDebugMode) { console.warn('stores/vehicle.js: refresh failed', error); }
          return this.vehicle;
        }

        if (!result.vehicle) {
          this.clear(CLEARED_BY_CATALOGUE);
          return null;
        }

        this.vehicle = result.vehicle;
        this.isStale = result.isStale;
        return this.vehicle;
      },

      /* ---------------------------------------------------------------- */
      /* Lifetime and cross-tab sync                                      */
      /* ---------------------------------------------------------------- */

      /**
       * Drop a vehicle nobody has used inside the time-to-live, otherwise slide the
       * window forward so an active shopper keeps theirs indefinitely.
       */
      expireOrSlide() {
        if (!this.hasVehicle) return;

        const savedAt = Number(this.vehicle.savedAt);
        if (Number.isFinite(savedAt) && Date.now() - savedAt * 1000 > savedTtlMs()) {
          this.clear('expired');
          return;
        }

        this.vehicle = { ...this.vehicle, savedAt: nowInSeconds() };
      },

      /**
       * Keep every open tab on the same vehicle. @alpinejs/persist writes the store to
       * localStorage, so another tab's save arrives here as a storage event.
       */
      watchOtherTabs() {
        window.addEventListener('storage', (event) => {
          if (event.key !== PERSIST_KEY) return;
          try {
            this.vehicle = event.newValue === null ? null : JSON.parse(event.newValue);
            this.isStale = false;
          } catch (error) {
            if (window.enableDebugMode) { console.warn('stores/vehicle.js: cross-tab sync failed', error); }
          }
        });
      },
    };
  },
};
